import { useEffect, useRef, useState } from 'react';

import { Breadcrumb, Button, Col, Flex, Input, List, Row, Spin } from 'antd';

import { faCheck, faChevronRight, faListCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// TODO: заменить надо будет на более гибкий компонент без привязки к конкретному API
export const ActivityCategoryList = ({
    categories,
    activityCategories,
    setActivityCategories,
    metadata,
    onLoadMore,
    multiselect = false,
    maxDepth = 3,
}: {
    categories: any[];
    activityCategories: any[];
    setActivityCategories: (categories: any[]) => void;
    metadata: any;
    onLoadMore: () => Promise<void>;
    multiselect?: boolean;
    maxDepth?: number;
}) => {
    const [currentCategories, setCurrentCategories] = useState([]);
    const [categoryPath, setCategoryPath] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        if (activityCategories?.length > 0) {
            console.log('Setting initial selected categories:', activityCategories);
            setSelectedItems(activityCategories);
        }
    }, [activityCategories]);

    useEffect(() => {
        setActivityCategories(selectedItems);
    }, [selectedItems]);

    // Инициализация категорий на основе текущего пути
    // Рекурсивный поиск категории по id в дереве
    const findCategoryById = (nodes, id) => {
        for (const node of nodes) {
            if (node.id === id) return node;
            if (node.subcategories?.length) {
                const found = findCategoryById(node.subcategories, id);
                if (found) return found;
            }
        }
        return null;
    };

    useEffect(() => {
        if (categories.length > 0) {
            if (categoryPath.length === 0) {
                setCurrentCategories(categories);
            } else {
                const targetCategoryId = categoryPath[categoryPath.length - 1].id;
                const targetCategory = findCategoryById(categories, targetCategoryId);
                const subcategories = targetCategory?.subcategories || [];

                console.log(`Setting subcategories for ${targetCategoryId}:`, subcategories.length);

                setCurrentCategories(subcategories);
            }
        }
    }, [categories, categoryPath]);

    // Настройка слушателя прокрутки для ручной бесконечной прокрутки
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

            // Когда мы достигаем почти дна и не загружаем уже данные
            if (scrollHeight - scrollTop - clientHeight < 50 && !isLoading) {
                // Проверяем, находимся ли мы на корневом уровне и есть ли ещё что загружать
                if (
                    categoryPath.length === 0 &&
                    metadata &&
                    metadata.current_page < metadata.last_page
                ) {
                    loadMoreData();
                }
            }
        };

        scrollContainer.addEventListener('scroll', handleScroll);

        return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }, [metadata, isLoading, categoryPath]);

    const loadMoreData = async () => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            await onLoadMore();
        } finally {
            setIsLoading(false);
        }
    };

    const navigateToCategory = (category) => {
        if (!hasSubcategories(category)) return;

        if (categoryPath.length >= maxDepth - 1) return;

        const newPath = [...categoryPath, category];
        setCategoryPath(newPath);
    };

    const navigateToBreadcrumb = (index) => {
        if (index === -1) {
            // Переход к корню
            setCategoryPath([]);
            console.log('Navigating to root');
            return;
        }

        // Переход к конкретному элементу хлебных крошек
        const newPath = categoryPath.slice(0, index + 1);
        setCategoryPath(newPath);
        console.log(`Navigating to breadcrumb index ${index}, new path:`, newPath);
    };

    const handleSelectCategory = (e, category) => {
        e.stopPropagation();

        if (multiselect) {
            setSelectedItems((prevSelected) => {
                const exists = prevSelected.some((i) => i.id === category.id);
                return exists
                    ? prevSelected.filter((i) => i.id !== category.id)
                    : [...prevSelected, category];
            });
        } else {
            setSelectedItems((prev) => (prev.some((i) => i.id === category.id) ? [] : [category]));
        }
    };

    const hasSubcategories = (category) => {
        // Проверяем, есть ли у этой категории подкатегории
        return category.subcategories?.length;
    };

    const isItemSelected = (category) => {
        if (!category || !selectedItems.length) return false;

        // Делаем точное строковое сравнение ID
        const selected = selectedItems.some((item) => item.id === category.id);

        return selected;
    };

    const getSelectedSubcategoriesCount = (category) => {
        if (!category || !selectedItems.length) return 0;
        // Подсчитываем, сколько выбранных элементов имеют эту категорию как родительскую

        return (
            category.subcategories?.filter((sub) =>
                selectedItems.find((selectedSub) => selectedSub.id === sub.id)
            ).length || 0
        );
    };

    const getTotalSubcategoriesCount = (category) => {
        if (!category) return 0;

        // Подсчитываем общее количество подкатегорий для этой категории
        return category.subcategories?.length || 0;
    };

    // Проверяем, находимся ли мы на корневом уровне (нет пути категории)
    const isRootLevel = categoryPath.length === 0;

    return (
        <div>
            <Row className="breadcrumbs-controls">
                <Col className="breadcrumbs">
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => navigateToBreadcrumb(-1)}>
                            <FontAwesomeIcon icon={faListCheck} />
                            <span className="crumb-name">Категории</span>
                        </Breadcrumb.Item>
                        {categoryPath.map((item, index) => (
                            <Breadcrumb.Item
                                key={item.id}
                                onClick={() => navigateToBreadcrumb(index)}
                            >
                                <span className="crumb-name">{item.name}</span>
                            </Breadcrumb.Item>
                        ))}
                    </Breadcrumb>
                </Col>
            </Row>

            <div style={{ margin: '10px 0' }}>
                {categoryPath.length === 0 && 'Выберите категорию'}
                {categoryPath.length === 1 && `Выберите подкатегорию в '${categoryPath[0]?.name}'`}
                {categoryPath.length === 2 && `Выберите тип в '${categoryPath[1]?.name}'`}
            </div>
            <Input.Search placeholder="Поиск недоступен" disabled />

            <div
                ref={scrollContainerRef}
                style={{
                    height: '400px',
                    overflow: 'auto',
                }}
            >
                <List
                    className="product-category-card-list"
                    dataSource={currentCategories}
                    itemLayout="horizontal"
                    locale={{
                        emptyText: isRootLevel
                            ? 'Нет доступных категорий'
                            : 'В этой категории нет подкатегорий',
                    }}
                    renderItem={(item) => {
                        const selected = isItemSelected(item);
                        const hasChildren = hasSubcategories(item);
                        const selectedCount = getSelectedSubcategoriesCount(item) || 0;
                        const totalCount = hasChildren ? getTotalSubcategoriesCount(item) : 0;

                        // Категория отключена на корневом уровне, если у неё нет подкатегорий
                        const isDisabled = isRootLevel && !hasChildren;
                        console.log(isRootLevel, !hasChildren);
                        return (
                            <List.Item
                                className="product-category-card"
                                onClick={() => !isDisabled && navigateToCategory(item)}
                                style={{
                                    paddingInline: '15px',
                                    cursor: hasChildren ? 'pointer' : 'default',
                                    backgroundColor: selected
                                        ? '#f0f7ff'
                                        : selectedCount > 0
                                        ? '#f8f8ff'
                                        : 'transparent',
                                    opacity: isDisabled ? 0.6 : 1,
                                }}
                            >
                                <Flex
                                    align="center"
                                    justify="space-between"
                                    style={{ width: '100%' }}
                                >
                                    <Col flex="auto">
                                        <Row align="middle">
                                            <Col>
                                                <h3
                                                    className="title"
                                                    style={{
                                                        color: isDisabled ? '#999' : 'inherit',
                                                        fontWeight:
                                                            selectedCount > 0 ? 'bold' : 'normal',
                                                        margin: 0,
                                                    }}
                                                >
                                                    {item.name}
                                                </h3>
                                            </Col>
                                            <Col>
                                                <div
                                                // style={{ marginLeft: 8 }}
                                                >
                                                    {hasChildren ? (
                                                        <span style={{ whiteSpace: 'nowrap' }}>
                                                            {selectedCount > 0 ? (
                                                                <span
                                                                    style={{
                                                                        color: '#1677ff',
                                                                        fontWeight: 'bold',
                                                                        paddingLeft: 4,
                                                                    }}
                                                                >
                                                                    Выбрано {selectedCount} из{' '}
                                                                    {totalCount}
                                                                </span>
                                                            ) : (
                                                                <span className="quantity">
                                                                    {/* Подкатегорий:  */}:{' '}
                                                                    {totalCount}
                                                                </span>
                                                            )}
                                                        </span>
                                                    ) : !isRootLevel ? null : (
                                                        <span
                                                            style={{
                                                                color: '#999',
                                                                fontStyle: 'italic',
                                                                fontSize: '12px',
                                                                // marginLeft: 5
                                                            }}
                                                        >
                                                            {/* (нет подкатегорий) */}: 0
                                                        </span>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col>
                                        {categoryPath.length < maxDepth - 1 ? (
                                            <Button
                                                icon={<FontAwesomeIcon icon={faChevronRight} />}
                                                shape="circle"
                                                type={selectedCount > 0 ? 'primary' : 'text'}
                                                disabled={isDisabled}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!isDisabled) navigateToCategory(item);
                                                }}
                                            />
                                        ) : (
                                            <Button
                                                icon={<FontAwesomeIcon icon={faCheck} />}
                                                onClick={(e) => handleSelectCategory(e, item)}
                                                shape="circle"
                                                type={selected ? 'primary' : 'default'}
                                            />
                                        )}
                                    </Col>
                                </Flex>
                            </List.Item>
                        );
                    }}
                />
                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin tip="Загрузка..." />
                    </div>
                )}
                {currentCategories.length === 0 && !isLoading && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        {isRootLevel
                            ? 'Нет доступных категорий'
                            : 'В этой категории нет подкатегорий'}
                    </div>
                )}
            </div>
            <div style={{ marginTop: 10 }}>
                {multiselect && `Выбрано подкатегорий: ${selectedItems.length}`}
                {metadata &&
                    isRootLevel &&
                    ` (отображено: ${currentCategories.length} из ${metadata.total})`}
            </div>
        </div>
    );
};
